import { motion } from "framer-motion";

const PAINS = [
  {
    title: "REALTOR DEPENDENCY",
    body: "You're borrowing someone else's relationship. When they stop referring, your pipeline stops.",
  },
  {
    title: "SHARED LEAD POOLS",
    body: "Zillow, LendingTree — leads who've already talked to 4 other LOs before they get to you.",
  },
  {
    title: "NO PIPELINE PREDICTABILITY",
    body: "You don't know if next month has 2 deals or 12. There's no system.",
  },
  {
    title: "1-ON-1 TIME DRAIN",
    body: "Every lead gets a separate consultation. You're trading time for pipeline instead of building one.",
  },
  {
    title: "ZERO RETARGETING ASSET",
    body: "When a lead doesn't convert, they're gone. No list. No follow-up. No second shot.",
  },
];

const MANIFESTO_WORDS_1 = "Every MLO knows this feeling.".split(" ");
const MANIFESTO_WORDS_2 = "Most accept it.".split(" ");
const MANIFESTO_WORDS_3 = "The ones who don't — they run WAM.".split(" ");

function Word({ word, delay, gold = false }) {
  return (
    <motion.span
      initial={{ opacity: 0, y: "0.4em" }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay }}
      className="inline-block mr-[0.25em]"
      style={{ color: gold ? "#C9A84C" : "#F0ECE4" }}
    >
      {word}
    </motion.span>
  );
}

export default function Pain() {
  return (
    <section
      id="the-problem"
      className="relative border-t border-b wam-hairline py-32 md:py-48"
      data-testid="pain-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 flex items-center gap-4"
        >
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#F0ECE4]/40">
            [002] —
          </span>
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#E05252]">
            THE OLD MODEL
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* LEFT — pain list */}
          <div>
            <ul className="space-y-8">
              {PAINS.map((p, i) => (
                <motion.li
                  key={p.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.7, delay: i * 0.09, ease: [0.16, 1, 0.3, 1] }}
                  className="border-t wam-hairline pt-6"
                  data-testid={`pain-item-${i}`}
                >
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-[24px] text-[#E05252] leading-none">
                      ✗
                    </span>
                    <div className="flex-1">
                      <div className="font-display text-[18px] md:text-[22px] tracking-[-0.02em] text-[#F0ECE4]">
                        {p.title}
                      </div>
                      <div className="mt-2 font-body text-[14px] leading-[1.6] text-[#F0ECE4]/60 max-w-[440px]">
                        {p.body}
                      </div>
                    </div>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* RIGHT — sticky manifesto */}
          <div className="relative">
            <div className="lg:sticky lg:top-32">
              <h2
                className="font-display leading-[0.95]"
                style={{ fontSize: "clamp(36px, 4.4vw, 62px)", letterSpacing: "-0.03em" }}
              >
                <div className="mb-3">
                  {MANIFESTO_WORDS_1.map((w, i) => (
                    <Word key={i} word={w} delay={0.05 * i} />
                  ))}
                </div>
                <div className="mb-3 opacity-70">
                  {MANIFESTO_WORDS_2.map((w, i) => (
                    <Word key={i} word={w} delay={0.35 + 0.05 * i} />
                  ))}
                </div>
                <div>
                  {MANIFESTO_WORDS_3.map((w, i) => (
                    <Word key={i} word={w} delay={0.7 + 0.05 * i} gold />
                  ))}
                </div>
              </h2>
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, delay: 1.2, ease: [0.85, 0, 0.15, 1] }}
                className="mt-10 h-px w-24 bg-[#C9A84C] origin-left"
              />
              <div className="mt-4 font-mono text-[10px] tracking-[0.3em] text-[#F0ECE4]/40">
                CONTINUE ↓
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
